import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import PaytmPaymentStatus from "./PaytmPaymentStatus";
import PaytmWaitScreen from "./PaytmWaitScreen";
import { connect } from "react-redux";
import { useHistory } from "react-router";
import { url_19, url_8, url_300, url_303 } from "../api_services";
import uiActions from "../../redux/ui/action";
import authActions from "../../redux/auth/action";
import {
  clearAwaitPaymentFromUrl,
  clearPendingOrderId,
  extractMid,
  extractOrderId,
  extractTxnAmount,
  extractTxnToken,
  getPendingOrderId,
  hasPaytmCallbackResourceHit,
  invokePaytmJsCheckout,
  isPaymentCompleted,
  isPaymentFailed,
  PAYTM_POLL_INTERVAL_MS,
  PAYTM_STATUS_MAX_ATTEMPTS,
  resolveGatewayPaymentMode,
  resolvePaymentFailureReason,
  shouldFinalizeFromStatusPoll,
  shouldFinalizePayment,
  wasReferredFromPaytmCallback,
} from "./paytmHelpers";

import { Analytics } from "aws-amplify";
import {
  clearWebEngagePaymentContext,
  getWebEngagePaymentContext,
  trackWebEngageCoursePurchased,
  trackWebEngagePaymentCompleted,
  trackWebEngagePaymentFailed,
} from "../webengage/webengageService";

const PaytmResponse = (props) => {
  const {
    orderId,
    userData,
    token,
    tokenType,
    transactionData,
    paytmDetails,
    startLoading,
    finishLoading,
    setUser,
  } = props;

  const {
    course_id,
    batch_id,
    package_id,
    txn_amount,
    txn_discount_amount,
    txn_date,
    package_duration,
    txnid,
  } = transactionData || {};

  const [paytmResponse, setPaytmResponse] = useState({});
  const [resolvedOrderId, setResolvedOrderId] = useState("");
  const [isReceiptLoading, setIsReceiptLoading] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const [isCheckingStatus, setIsCheckingStatus] = useState(false);
  const [isPendingVerification, setIsPendingVerification] = useState(false);
  const history = useHistory();
  const pollCleanupRef = useRef(null);
  const checkoutSubmittedRef = useRef(false);
  const paymentSettledRef = useRef(false);
  const gatewayReturnedRef = useRef(false);
  const statusCheckStartedRef = useRef(false);
  const pollingStartedRef = useRef(false);
  const failedPaymentTrackedRef = useRef(new Set());
  const purchasedTrackedRef = useRef(new Set());

  const getOrderIdFromUrl = () => {
    const params = new URLSearchParams(window.location.search);
    return (
      params.get("order_id") ||
      params.get("ORDERID") ||
      params.get("orderId") ||
      getPendingOrderId() ||
      ""
    );
  };

  const isAwaitingPayment = () => {
    const params = new URLSearchParams(window.location.search);
    return params.get("await_payment") === "1";
  };

  const isPaymentReturn = () => {
    const params = new URLSearchParams(window.location.search);
    return params.get("payment_return") === "1";
  };

  const recordAnalytics = async (eventName) => {
    try {
      await Analytics.record({
        name: eventName,
        attributes: {
          course_id: course_id,
          batch_id: batch_id,
          itemprice: txn_amount,
          product_id: package_id,
          transactionDate: txn_date,
          user_name: userData?.name,
          mobile: userData?.mobile,
          userId: userData?.user_id,
        },
      });
    } catch (error) {
      // Amplify/Pinpoint misconfig should never block WebEngage or payment UI.
      console.warn("[Analytics] Amplify record skipped:", eventName, error?.message || error);
    }
  };

  const getPaymentEventAttributes = (responseData = {}, extraAttributes = {}) => {
    const savedContext = getWebEngagePaymentContext();
    const currentOrderId =
      orderId ||
      resolvedOrderId ||
      getOrderIdFromUrl() ||
      txnid ||
      savedContext.order_id;
    const transactionId =
      responseData?.txnId ||
      responseData?.TXNID ||
      responseData?.transaction_id ||
      txnid ||
      savedContext.transaction_id ||
      currentOrderId;
    const programName =
      savedContext.program_name || savedContext.product_name;
    const receiptAmount =
      responseData?.receipt?.amount ??
      responseData?.data?.receipt?.amount ??
      responseData?.orderStatus?.TXNAMOUNT ??
      responseData?.txn_amount;

    const pickAmount = (...candidates) => {
      for (const value of candidates) {
        if (value == null || value === "") continue;
        const numeric = Number(value);
        if (Number.isFinite(numeric) && numeric > 0) return value;
      }
      return undefined;
    };

    const programPrice = pickAmount(
      txn_amount,
      savedContext.final_price,
      savedContext.total_amount,
      savedContext.amount,
      savedContext.program_price,
      receiptAmount
    );
    const specialPrice = pickAmount(
      savedContext.special_price,
      savedContext.discounted_price,
      programPrice
    );
    const discountAmount =
      txn_discount_amount ?? savedContext.discount_amount ?? 0;

    return {
      program_name: programName,
      product_name: programName,
      program_details: savedContext.program_details,
      program_price: pickAmount(savedContext.program_price, programPrice),
      final_price: programPrice,
      special_price: specialPrice,
      coupon_code: savedContext.coupon_code,
      coupon_mode: savedContext.coupon_mode,
      discount_amount: discountAmount,
      transaction_id: transactionId,
      order_id: currentOrderId,
      total_amount: programPrice,
      amount: programPrice,
      source: savedContext.source || "web",
      product_id: package_id || savedContext.product_id,
      ...extraAttributes,
      payment_mode:
        resolveGatewayPaymentMode(responseData) ||
        resolveGatewayPaymentMode(extraAttributes) ||
        savedContext.payment_mode,
    };
  };

  const recordWebEngagePaymentSuccess = (responseData = {}) => {
    const attributes = getPaymentEventAttributes(responseData);
    const deduplicationKey =
      attributes.order_id || `${attributes.product_id}-${txn_date || "purchase"}`;

    if (purchasedTrackedRef.current.has(deduplicationKey)) {
      console.warn(
        "[WebEngage] Payment success already tracked for",
        deduplicationKey
      );
      return;
    }
    purchasedTrackedRef.current.add(deduplicationKey);

    // Course Purchased first (on success), then Payment Completed.
    trackWebEngageCoursePurchased(attributes);
    trackWebEngagePaymentCompleted(attributes);
    clearWebEngagePaymentContext();
  };

  const recordWebEngagePaymentFailure = (responseData = {}) => {
    const reason = resolvePaymentFailureReason(responseData);
    const attributes = getPaymentEventAttributes(responseData, { reason });
    const deduplicationKey =
      attributes.order_id || `${attributes.product_id}-${txn_date || "failure"}`;

    if (failedPaymentTrackedRef.current.has(deduplicationKey)) {
      console.warn(
        "[WebEngage] Payment failure already tracked for",
        deduplicationKey
      );
      return;
    }
    failedPaymentTrackedRef.current.add(deduplicationKey);

    trackWebEngagePaymentFailed(attributes);
    clearWebEngagePaymentContext();
  };

  const showFailureResult = async (seedResponse, { refresh = true } = {}) => {
    if (paymentSettledRef.current) return;
    paymentSettledRef.current = true;
    stopPolling();
    setIsPendingVerification(false);
    setIsCheckingStatus(false);
    setShowCheckout(false);
    setShowResult(true);

    const currentOrderId = orderId || getOrderIdFromUrl();
    let failureData =
      seedResponse && typeof seedResponse === "object"
        ? seedResponse
        : { paymentStatus: "Failed" };

    // Refresh only when we don't already have a fresh status payload.
    if (refresh && currentOrderId) {
      try {
        const responseData = await fetchTransactionStatus(currentOrderId);
        if (responseData.status === 200) {
          failureData = responseData;
        }
      } catch (error) {
        console.log("Error fetching failure transaction status:", error);
      }
    }

    setPaytmResponse(failureData);
    clearAwaitPaymentFromUrl(history);
    recordWebEngagePaymentFailure(failureData);
    await recordAnalytics("web_purchased_failed");
    clearPendingOrderId();
  };

  const showPendingVerificationResult = async (
    lastResponse = {},
    { refresh = true } = {}
  ) => {
    if (paymentSettledRef.current) return;
    paymentSettledRef.current = true;
    stopPolling();
    setShowCheckout(false);
    setIsCheckingStatus(false);
    setIsPolling(false);
    setShowResult(true);
    setIsPendingVerification(true);

    const currentOrderId = orderId || getOrderIdFromUrl();
    let pendingData = {
      paymentStatus: "Pending",
      receipt: {
        heading: "Pending Verification",
        sub_message:
          "Your payment is still being verified. This can take a few minutes.",
      },
      ...lastResponse,
    };

    if (refresh && currentOrderId) {
      try {
        const responseData = await fetchTransactionStatus(currentOrderId);
        if (responseData.status === 200) {
          pendingData = responseData;
        }
      } catch (error) {
        console.log("Error fetching pending transaction status:", error);
      }
    }

    setPaytmResponse(pendingData);
    clearAwaitPaymentFromUrl(history);
  };

  const handlePaymentResult = async (
    responseData,
    { refresh = true } = {}
  ) => {
    if (paymentSettledRef.current) return;
    paymentSettledRef.current = true;
    stopPolling();
    setIsPendingVerification(false);
    setIsCheckingStatus(false);
    setShowCheckout(false);
    setShowResult(true);

    let finalData = responseData || {};
    const currentOrderId = orderId || getOrderIdFromUrl();

    // Refresh only when we don't already have a fresh status payload from polling.
    if (refresh && currentOrderId) {
      try {
        const freshData = await fetchTransactionStatus(currentOrderId);
        if (freshData.status === 200) {
          finalData = freshData;
        }
      } catch (error) {
        console.log("Error refreshing transaction status for result:", error);
      }
    }

    setPaytmResponse(finalData);
    clearAwaitPaymentFromUrl(history);
    clearPendingOrderId();

    if (isPaymentCompleted(finalData)) {
      saveUserTransactions();
      recordWebEngagePaymentSuccess(finalData);
      await recordAnalytics("web_purchased_success");
    } else {
      recordWebEngagePaymentFailure(finalData);
      await recordAnalytics("web_purchased_failed");
    }
  };

  const fetchTransactionStatus = async (currentOrderId) => {
    const headers = {
      Authorization: `${tokenType} ${token}`,
    };

    const response = await axios.get(
      `${url_300}?order_id=${encodeURIComponent(currentOrderId)}`,
      { headers }
    );

    return response.data;
  };

  const getPaytmResponse = async () => {
    const currentOrderId = orderId || getOrderIdFromUrl();
    if (!currentOrderId) return;
    setResolvedOrderId(currentOrderId);

    try {
      const responseData = await fetchTransactionStatus(currentOrderId);
      if (
        responseData.status === 200 &&
        shouldFinalizePayment(responseData)
      ) {
        await handlePaymentResult(responseData);
      } else {
        await showFailureResult();
      }
    } catch (error) {
      console.log("Error checking Paytm transaction status:", error);
      await showFailureResult();
    }
  };

  const stopPolling = () => {
    if (pollCleanupRef.current) {
      pollCleanupRef.current();
      pollCleanupRef.current = null;
    }
    pollingStartedRef.current = false;
  };

  /**
   * Poll status only AFTER Paytm activity (success / cancel / decline return).
   * Cap at PAYTM_STATUS_MAX_ATTEMPTS calls. Never call while gateway is open.
   */
  const startPostGatewayStatusPolling = (currentOrderId) => {
    if (pollingStartedRef.current || paymentSettledRef.current) return;
    pollingStartedRef.current = true;

    let settled = false;
    let attempts = 0;
    let lastResponse = null;

    const cleanup = () => {
      clearInterval(pollTimer);
      setIsPolling(false);
      pollCleanupRef.current = null;
      pollingStartedRef.current = false;
    };

    const settleTerminal = async (responseData) => {
      if (settled || paymentSettledRef.current) return;
      settled = true;
      cleanup();
      setIsCheckingStatus(false);
      await handlePaymentResult(responseData, { refresh: false });
    };

    // After max polls still not Completed/Failed (typical user cancel) → Unsuccessful.
    const settleCancelledAsUnsuccessful = async (responseData) => {
      if (settled || paymentSettledRef.current) return;
      settled = true;
      cleanup();
      setIsCheckingStatus(false);
      await showFailureResult(
        {
          ...(responseData || {}),
          paymentStatus: "Failed",
        },
        { refresh: false }
      );
    };

    const pollStatus = async () => {
      if (settled || paymentSettledRef.current) return true;
      if (attempts >= PAYTM_STATUS_MAX_ATTEMPTS) return true;

      attempts += 1;

      try {
        const responseData = await fetchTransactionStatus(currentOrderId);
        if (responseData.status !== 200) {
          if (attempts >= PAYTM_STATUS_MAX_ATTEMPTS) {
            await settleCancelledAsUnsuccessful(lastResponse || {});
            return true;
          }
          return false;
        }

        lastResponse = responseData;

        if (shouldFinalizeFromStatusPoll(responseData)) {
          await settleTerminal(responseData);
          return true;
        }

        if (attempts >= PAYTM_STATUS_MAX_ATTEMPTS) {
          await settleCancelledAsUnsuccessful(responseData);
          return true;
        }
      } catch (error) {
        console.log("Error polling Paytm transaction status:", error);
        if (attempts >= PAYTM_STATUS_MAX_ATTEMPTS) {
          await settleCancelledAsUnsuccessful(lastResponse || {});
          return true;
        }
      }
      return false;
    };

    setIsPolling(true);
    pollStatus();

    const pollTimer = setInterval(async () => {
      if (settled || paymentSettledRef.current) return;
      await pollStatus();
    }, PAYTM_POLL_INTERVAL_MS);

    pollCleanupRef.current = cleanup;
  };

  /** Hide gateway, show full-screen loader, then poll status until Completed/Failed. */
  const beginStatusCheckAfterGatewayActivity = (reason = "unknown") => {
    if (paymentSettledRef.current || statusCheckStartedRef.current) {
      console.log("[Paytm][cancel] status check skipped (already started)", {
        reason,
        paymentSettled: paymentSettledRef.current,
        statusCheckStarted: statusCheckStartedRef.current,
      });
      return;
    }

    console.log("[Paytm][cancel] beginStatusCheckAfterGatewayActivity", {
      reason,
      orderId: orderId || getOrderIdFromUrl(),
    });

    gatewayReturnedRef.current = true;
    statusCheckStartedRef.current = true;

    const currentOrderId = orderId || getOrderIdFromUrl();
    setShowCheckout(false);
    setIsCheckingStatus(true);
    setIsPolling(true);

    if (!currentOrderId) {
      console.warn("[Paytm][cancel] no order id — showing failure");
      setIsCheckingStatus(false);
      showFailureResult();
      return;
    }

    startPostGatewayStatusPolling(currentOrderId);
  };

  const handleCheckStatusAgain = async () => {
    const currentOrderId = resolvedOrderId || orderId || getOrderIdFromUrl();
    if (!currentOrderId) return;

    setIsPolling(true);
    try {
      const responseData = await fetchTransactionStatus(currentOrderId);

      if (responseData.status !== 200) return;

      if (
        isPaymentCompleted(responseData) ||
        shouldFinalizePayment(responseData) ||
        isPaymentFailed(responseData)
      ) {
        paymentSettledRef.current = false;
        setIsPendingVerification(false);
        await handlePaymentResult(responseData);
        return;
      }

      setPaytmResponse(responseData);
      setIsPendingVerification(true);
      setShowResult(true);
    } catch (error) {
      console.log("Error checking payment status again:", error);
    } finally {
      setIsPolling(false);
    }
  };

  useEffect(() => {
    const currentOrderId = orderId || getOrderIdFromUrl();
    console.log("[Paytm][checkout] gateway page mount", {
      currentOrderId,
      awaitPayment: isAwaitingPayment(),
      paymentReturn: isPaymentReturn(),
      hasPaytmDetails: Boolean(Object.keys(paytmDetails || {}).length),
      referrer: document.referrer,
      wasReferredFromCallback: wasReferredFromPaytmCallback(),
      callbackResourceHit: hasPaytmCallbackResourceHit(),
    });
    if (!currentOrderId) return;
    setResolvedOrderId(currentOrderId);

    if (
      isPaymentReturn() ||
      wasReferredFromPaytmCallback() ||
      hasPaytmCallbackResourceHit()
    ) {
      beginStatusCheckAfterGatewayActivity("mount_payment_return");
    } else if (isAwaitingPayment() && Object.keys(paytmDetails || {}).length) {
      console.log("[Paytm][checkout] opening CheckoutJS (redirect:false)");
      setShowCheckout(true);
    } else {
      console.log("[Paytm][checkout] not awaiting payment — fetch status only");
      getPaytmResponse();
    }

    return () => {
      stopPolling();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!showCheckout || checkoutSubmittedRef.current) return;
    if (!Object.keys(paytmDetails || {}).length) return undefined;

    let cancelled = false;

    const openCheckout = async () => {
      const mid = extractMid(paytmDetails);
      const checkoutOrderId =
        extractOrderId(paytmDetails) || orderId || getOrderIdFromUrl();
      const txnToken = extractTxnToken(paytmDetails);
      const amount = extractTxnAmount(paytmDetails) || txn_amount || "";

      console.log("[Paytm][checkout] invoke fields", {
        mid,
        checkoutOrderId,
        hasTxnToken: Boolean(txnToken),
        amount,
      });

      try {
        await invokePaytmJsCheckout({
          mid,
          orderId: checkoutOrderId,
          txnToken,
          amount,
          rootId: "",
          onPaymentActivity: (type, payload) => {
            if (cancelled) return;
            console.log("[Paytm][cancel] CheckoutJS activity payload", payload);
            beginStatusCheckAfterGatewayActivity(`checkoutjs:${type}`);
          },
        });
        if (!cancelled) checkoutSubmittedRef.current = true;
      } catch (error) {
        console.error("[Paytm][checkout] CheckoutJS open failed", error);
        if (!cancelled) {
          checkoutSubmittedRef.current = true;
          showFailureResult();
        }
      }
    };

    const timer = setTimeout(openCheckout, 50);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCheckout, paytmDetails]);

  const renderStatusCheckingLoader = () => (
    <PaytmWaitScreen
      mode={isAwaitingPayment() && !isCheckingStatus ? "opening" : "checking"}
    />
  );

  // When user clicks browser back arrow, go to dashboard only after showing result.
  const showResultRef = useRef(false);
  useEffect(() => {
    showResultRef.current = Boolean(showResult);
  }, [showResult]);

  useEffect(() => {
    const onPopState = () => {
      if (!showResultRef.current) return;
      history.replace("/dashboard");
    };

    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveUserTransactions = async () => {
    const headers = {
      Authorization: `${tokenType} ${token}`,
    };
    let dataDetails = {
      course_id,
      batch_id,
      package_id,
      txn_amount,
      txn_discount_amount,
      txn_date,
      package_duration,
      txnid,
    };

    startLoading();
    try {
      const response = await axios.post(url_19, dataDetails, { headers });
      if (response.data.status === 200) {
        finishLoading();
        await updateUser();
      }
    } catch (error) {
      console.log(error);
      finishLoading();
    }
  };

  const updateUser = async () => {
    startLoading();
    try {
      const response = await axios.get(url_8);
      if (response.data.status === 200) {
        setUser(response.data.data);
        finishLoading();
        return 1;
      }
      return 0;
    } catch (error) {
      console.log(error);
      return 0;
    }
  };

  const triggerPdfDownload = (blob, fileName) => {
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = objectUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(objectUrl);
  };

  const downloadReceipt = async () => {
    const currentOrderId =
      resolvedOrderId || orderId || getOrderIdFromUrl();
    if (!currentOrderId) {
      alert("Order id not found.");
      return;
    }

    setIsReceiptLoading(true);
    try {
      const response = await axios.get(url_303, {
        params: { order_id: currentOrderId },
        headers: {
          Authorization: `${tokenType} ${token}`,
        },
        responseType: "blob",
      });

      const contentType = response.headers?.["content-type"] || "";

      if (contentType.includes("application/pdf")) {
        triggerPdfDownload(
          new Blob([response.data], { type: "application/pdf" }),
          `receipt-${currentOrderId}.pdf`
        );
        return;
      }

      const responseText = await response.data.text();
      let payload = {};
      try {
        payload = JSON.parse(responseText);
      } catch (parseError) {
        console.log("Error parsing receipt response:", parseError);
      }

      const msg = payload?.message;
      alert(
        typeof msg === "string" && msg
          ? msg
          : "Could not download receipt."
      );
    } catch (error) {
      console.log("Error downloading receipt:", error);
      const errorPayload = error?.response?.data;
      let message = "";

      if (errorPayload instanceof Blob) {
        try {
          const parsed = JSON.parse(await errorPayload.text());
          message = parsed?.message || "";
        } catch (parseError) {
          console.log("Error parsing receipt error response:", parseError);
        }
      } else {
        message = errorPayload?.message || error?.message || "";
      }

      alert(message || "Could not download receipt. Please try again.");
    } finally {
      setIsReceiptLoading(false);
    }
  };

  const renderResult = () => (
    <PaytmPaymentStatus
      paytmResponse={paytmResponse}
      transactionData={transactionData}
      onDownloadReceipt={downloadReceipt}
      isReceiptLoading={isReceiptLoading}
      isPendingVerification={isPendingVerification}
      onCheckStatusAgain={handleCheckStatusAgain}
      isCheckingStatus={isPolling}
    />
  );

  // CheckoutJS (root:"") paints its own full-page overlay on top of this once loaded.
  if (showCheckout) {
    return <PaytmWaitScreen mode="opening" />;
  }

  if (isCheckingStatus && !showResult) {
    return renderStatusCheckingLoader();
  }

  if (showResult && (Object.keys(paytmResponse).length || isPendingVerification)) {
    return renderResult();
  }

  return renderStatusCheckingLoader();
};

const mapStateToProps = (state) => {
  return {
    orderId: state.paymentReducer.orderId,
    userData: state.auth.user,
    token: state.auth.authToken,
    tokenType: state.auth.authType,
    transactionData: state.paymentReducer.transactionDetails,
    paytmDetails: state.paymentReducer.paytmDetails,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    startLoading: () => {
      dispatch(uiActions.startLoading());
    },
    finishLoading: () => {
      dispatch(uiActions.finishLoading());
    },
    setUser: (payload) => {
      dispatch(authActions.setUser(payload));
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(PaytmResponse);
